import React, { useEffect, useState } from "react";
import Modal, { ModalFooter } from "@amzn/meridian/modal";
import Row from "@amzn/meridian/row";
import Button from "@amzn/meridian/button";
import Text from "@amzn/meridian/text";
import FileInput, { FileDetails } from "@amzn/meridian/file-input";
import Column from "@amzn/meridian/column";
import { Storage } from "aws-amplify";
import { v4 as uuidv4 } from "uuid";
import _ from "lodash";

interface FileUploadModalProps {
    data;
    open: boolean;
    onClose: () => void;
    onFileUpload: (file: File, s3Key: string) => void;
}

export const FileUploadModal: React.FC<FileUploadModalProps> = ({
    data,
    open,
    onClose,
    onFileUpload,
}) => {
    const [file, setFile] = useState<File | null>(null);
    const [percentage, setPercentage] = useState<number | null>(null);
    const [isUploading, setIsUploading] = useState(false);

    const onFileAttached = (acceptedFiles: File[]) => {
        const newFile = acceptedFiles[0];
        setFile(newFile);
        setPercentage(0);
    };

    const onClickCancel = () => {
        setFile(null);
        setPercentage(null);
    };

    useEffect(() => {
        onClickCancel();
    }, []);

    const uploadToS3 = async () => {
        if (!file) return;

        setIsUploading(true);
        const s3Key = `APPRAISER-AI/APPEALS/ATTACHMENTS/${_.get(data, "appealArtifactId")}/${uuidv4()}.pdf`;

        try {
            await Storage.put(s3Key, file, {
                progressCallback(progress) {
                    const percentUploaded = Math.floor((progress.loaded / progress.total) * 100);
                    setPercentage(percentUploaded);
                },
            });

            onFileUpload(file, s3Key);
            onCloseModal();
        } catch (error) {
            console.error("Error uploading file:", error);
        } finally {
            setIsUploading(false);
        }
    };

    const onCloseModal = () => {
        onClickCancel();
        onClose();
    };

    return (
        <Modal
            title="Upload Attachment"
            open={open}
            onClose={onCloseModal}
            scrollContainer="viewport"
            closeLabel="Close"
            aria-describedby="modal-description"
            width={"600px"}
        >
            <Column spacing={"400"} width={"100%"}>
                <Text id="modal-description">Select a file to upload</Text>
                <FileInput
                    type="single"
                    onFileAttached={onFileAttached}
                    accept={["application/pdf"].join(",")}
                >
                    {file && (
                        <FileDetails
                            file={file}
                            onClickCancel={onClickCancel}
                            cancelUploadButtonLabel={`Cancel, ${file.name}`}
                            uploadPercentage={percentage}
                            uploadComplete={percentage === 100}
                        />
                    )}
                </FileInput>
            </Column>
            <ModalFooter>
                <Row alignmentHorizontal="end" widths="fit">
                    <Button
                        type="primary"
                        size="small"
                        onClick={uploadToS3}
                        disabled={!file || isUploading}
                    >
                        {isUploading ? "Uploading..." : "Upload"}
                    </Button>
                    <Button type="secondary" size="small" onClick={onClose}>
                        Close
                    </Button>
                </Row>
            </ModalFooter>
        </Modal>
    );
};
